/**
 * Servis helpers — slot math + event-type tone mapping.
 *
 * Slot range matches the legacy and the radical artboard: 7:00 → 17:00, with
 * 30-minute increments → 20 slots per day. The legacy renderer uses the same
 * range (see `src/servis/index.js:154`).
 *
 * `typ_udalosti` discriminates event kind in the database:
 *   0 = servisní úkon (regular service task)
 *   1 = dovolená (vacation)
 *   2 = interní servisní úkon
 *   3 = směna (shift)
 *
 * Mapped to the four locked semantic tones (accent / warn / ok / neutral).
 * The design bundle's 5-kind palette (urgent / shift / service-light /
 * service-heavy / pdi) is collapsed here to the locked set.
 */

import type { EventChipTone } from '@/components/EventChip';
import type {
  ServisDay,
  ServisDruhUkonu,
  ServisEvent,
  ServisEventDetail,
} from './api';

export const SLOT_START_HOUR = 7;
export const SLOT_END_HOUR = 17;
export const SLOTS_PER_DAY = (SLOT_END_HOUR - SLOT_START_HOUR) * 2;

export const SLOT_LABELS: string[] = (() => {
  const labels: string[] = [];
  for (let i = 0; i < SLOTS_PER_DAY; i++) {
    const h = SLOT_START_HOUR + Math.floor(i / 2);
    const m = i % 2 === 0 ? '00' : '30';
    labels.push(`${h}:${m.padStart(2, '0')}`);
  }
  return labels;
})();

const MONTH_ABBR_CS = [
  'led', 'úno', 'bře', 'dub', 'kvě', 'čvn',
  'čvc', 'srp', 'zář', 'říj', 'lis', 'pro',
];

/**
 * Format a date string (YYYY-MM-DD) as "27. dub. 26" for the swimlane day
 * header. Mirrors the legacy `formatDatumNadpis` (src/servis/index.js:101).
 */
export function formatDayDate(dateStr: string): string {
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const day = parseInt(parts[2], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[0], 10);
  if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) return dateStr;
  const monthLabel = MONTH_ABBR_CS[month] ?? '';
  return `${day}. ${monthLabel}. ${String(year).slice(-2)}`;
}

/**
 * Format a date range like "27. dub – 1. kvě 2026" used in the band header.
 * The artboard renders the year only on the right edge.
 */
export function formatWeekRange(monday: string, sunday: string): string {
  const m = monday.split('-').map((p) => parseInt(p, 10));
  const s = sunday.split('-').map((p) => parseInt(p, 10));
  if (m.length !== 3 || s.length !== 3) return `${monday} – ${sunday}`;
  const [, mMonth, mDay] = m;
  const [sYear, sMonth, sDay] = s;
  const left = `${mDay}. ${MONTH_ABBR_CS[mMonth - 1]}`;
  const right = `${sDay}. ${MONTH_ABBR_CS[sMonth - 1]} ${sYear}`;
  return `${left} – ${right}`;
}

const TONE_BY_TYPE: Record<number, EventChipTone> = {
  0: 'ok',
  1: 'warn',
  2: 'neutral',
  3: 'accent',
};

export function eventTone(event: Pick<ServisEvent, 'typ_udalosti'>): EventChipTone {
  return TONE_BY_TYPE[event.typ_udalosti] ?? 'ink';
}

/** How an event shows its confirmation ("did it happen?"). */
export type PotvrzeniStatus = 'probehlo' | 'neprobehlo' | 'k_potvrzeni';

/** Marker tone per status — shared by the Plánování inspector and Vyhledání. */
export const POTVRZENI_MARKER_TONE: Record<PotvrzeniStatus, 'ok' | 'danger' | 'warn'> = {
  probehlo: 'ok',
  neprobehlo: 'danger',
  k_potvrzeni: 'warn',
};

export const POTVRZENI_LABEL: Record<PotvrzeniStatus, string> = {
  probehlo: 'Proběhlo',
  neprobehlo: 'Neproběhlo',
  k_potvrzeni: 'K potvrzení',
};

/**
 * Confirmation status for chips, markers and lists: the stored state, else
 * `k_potvrzeni` when the server flags the event (started within the last 72 h,
 * still unconfirmed), else null (nothing to show — future events, old
 * unconfirmed ones, vacations and shifts).
 */
export function potvrzeniStatus(
  event: Pick<ServisEvent, 'potvrzeni_stav' | 'k_potvrzeni'>,
): PotvrzeniStatus | null {
  if (event.potvrzeni_stav) return event.potvrzeni_stav;
  return event.k_potvrzeni ? 'k_potvrzeni' : null;
}

/** Chip tone: a no-show turns the chip red, everything else keeps the type tone. */
export function eventChipTone(
  event: Pick<ServisEvent, 'typ_udalosti' | 'potvrzeni_stav'>,
): EventChipTone {
  return event.potvrzeni_stav === 'neprobehlo' ? 'danger' : eventTone(event);
}

/** "Jana Malá · 30. 9. 2026 12:07" — who set the confirmation and when. */
export function formatPotvrzeno(
  detail: Pick<ServisEventDetail, 'potvrzeno_at' | 'potvrdil_jmeno' | 'potvrdil_prijmeni'>,
): string {
  const who = [detail.potvrdil_jmeno, detail.potvrdil_prijmeni]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(' ');
  const when = detail.potvrzeno_at ? formatDatumCas(detail.potvrzeno_at) : '';
  return [who, when].filter(Boolean).join(' · ');
}

/**
 * Compute the (start, span) slot range for an event within a given day.
 * Events that overflow the visible window get clamped to [0, SLOTS_PER_DAY].
 *
 * A multi-day event continuing from a previous day is clamped to the day's
 * 7:00 window start — but if a leading shift block (typ 3) marks the mechanic's
 * non-working start-of-day window (e.g. an "Odpolední směna" block 7:00–8:30),
 * the continuing event resumes where that block ends instead, so the two chips
 * don't draw on top of each other. The symmetric case applies at the end of the
 * day: an event spilling into the next day stops where a trailing shift block
 * (e.g. a "Ranní směna" block 15:30–17:00) begins. `day.events` is the full day
 * roster; only shift blocks for the same mechanic are considered.
 */
export function eventSlotRange(
  event: Pick<ServisEvent, 'datum_od' | 'datum_do'> & Partial<Pick<ServisEvent, 'id_opravce'>>,
  day: Pick<ServisDay, 'date'> & Partial<Pick<ServisDay, 'events'>>,
): { start: number; span: number } | null {
  const od = parseDateTime(event.datum_od);
  const dod = parseDateTime(event.datum_do);
  if (!od || !dod) return null;

  const dayStart = parseDate(day.date);
  if (!dayStart) return null;
  dayStart.setHours(SLOT_START_HOUR, 0, 0, 0);
  const dayEnd = new Date(dayStart);
  dayEnd.setHours(SLOT_END_HOUR, 0, 0, 0);

  let visibleOd = od < dayStart ? dayStart : od;
  let visibleDod = dod > dayEnd ? dayEnd : dod;

  // Only events that spill over a day boundary get pulled clear of a shift
  // block — an event that genuinely starts/ends today keeps its own time.
  if (day.events && event.id_opravce != null) {
    if (od < dayStart) {
      visibleOd = skipLeadingShifts(visibleOd, dayEnd, event.id_opravce, day.events);
    }
    if (dod > dayEnd) {
      visibleDod = skipTrailingShifts(visibleDod, dayStart, event.id_opravce, day.events);
    }
  }

  if (visibleDod <= visibleOd) return null;

  const minutesFromStart =
    (visibleOd.getTime() - dayStart.getTime()) / 60_000;
  const minutesSpan = (visibleDod.getTime() - visibleOd.getTime()) / 60_000;

  const startSlot = Math.max(0, Math.round(minutesFromStart / 30));
  const span = Math.max(1, Math.round(minutesSpan / 30));
  if (startSlot >= SLOTS_PER_DAY) return null;
  return { start: startSlot, span: Math.min(span, SLOTS_PER_DAY - startSlot) };
}

/**
 * Advance `start` past any back-to-back shift blocks (typ 3) for `mechId` that
 * cover the start-of-day window, capped at `dayEnd`. Returns `start` unchanged
 * when no leading shift block overlaps it.
 */
function skipLeadingShifts(
  start: Date,
  dayEnd: Date,
  mechId: number,
  events: ServisDay['events'],
): Date {
  let cursor = start;
  let advanced = true;
  while (advanced) {
    advanced = false;
    for (const ev of events) {
      if (ev.typ_udalosti !== 3 || ev.id_opravce !== mechId) continue;
      const sOd = parseDateTime(ev.datum_od);
      const sDod = parseDateTime(ev.datum_do);
      if (!sOd || !sDod) continue;
      // Shift block straddles the cursor → resume after it ends.
      if (sOd <= cursor && sDod > cursor) {
        cursor = sDod > dayEnd ? dayEnd : sDod;
        advanced = true;
      }
    }
  }
  return cursor;
}

/**
 * Pull `end` back before any back-to-back shift blocks (typ 3) for `mechId` that
 * cover the end-of-day window, floored at `dayStart`. Returns `end` unchanged
 * when no trailing shift block overlaps it.
 */
function skipTrailingShifts(
  end: Date,
  dayStart: Date,
  mechId: number,
  events: ServisDay['events'],
): Date {
  let cursor = end;
  let retreated = true;
  while (retreated) {
    retreated = false;
    for (const ev of events) {
      if (ev.typ_udalosti !== 3 || ev.id_opravce !== mechId) continue;
      const sOd = parseDateTime(ev.datum_od);
      const sDod = parseDateTime(ev.datum_do);
      if (!sOd || !sDod) continue;
      // Shift block straddles the cursor → stop where it begins.
      if (sDod >= cursor && sOd < cursor) {
        cursor = sOd < dayStart ? dayStart : sOd;
        retreated = true;
      }
    }
  }
  return cursor;
}

function parseDateTime(s: string | null | undefined): Date | null {
  if (!s) return null;
  // Server returns "YYYY-MM-DD HH:MM:SS" — convert to ISO-like before parsing.
  const iso = s.replace(' ', 'T');
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseDate(s: string): Date | null {
  const parts = s.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

/**
 * The úkon as displayed: an internal event's kind leads its free text
 * ("STK · výměna brzd"); either part may be missing.
 */
export function ukonText(event: Pick<ServisEvent, 'druh_ukonu' | 'servisni_ukon'>): string {
  const druh = event.druh_ukonu ? DRUH_UKONU_LABEL[event.druh_ukonu] : '';
  return [druh, (event.servisni_ukon ?? '').trim()].filter(Boolean).join(' · ');
}

/**
 * Compose the displayed event title — falls back to a sensible default for
 * each `typ_udalosti` so empty rows still show something meaningful.
 */
export function eventTitle(event: ServisEvent): string {
  const candidates = [event.jmeno_zakaznika, event.vozidlo, ukonText(event)];
  for (const c of candidates) {
    const trimmed = (c ?? '').trim();
    if (trimmed) return trimmed;
  }
  switch (event.typ_udalosti) {
    case 1:
      return 'Dovolená';
    case 2:
      return 'Interní úkon';
    case 3:
      return 'Směna';
    default:
      return 'Servisní úkon';
  }
}

/**
 * Author-dot color for a servis event — derived from the creator's
 * `uzivatele.barva`. Thin alias over the shared `userDotColor`.
 */
export { userDotColor as authorColor } from '@/lib/color';

/**
 * Compose the chip / inspector subtitle. Mirrors the legacy chip's lines 2+3
 * (`servis_utils.js:104`: "SPZ vozidlo" then "servisní úkon") so the vehicle
 * is visible without opening the event. A field already promoted to the
 * title (e.g. `vozidlo` when there's no customer name) isn't repeated.
 */
export function eventSubtitle(event: ServisEvent): string | undefined {
  const title = eventTitle(event);
  const usedAsTitle = (s: string | null | undefined) => (s ?? '').trim() === title;
  const vehicle = [event.spz_vozidla || event.vin, usedAsTitle(event.vozidlo) ? '' : event.vozidlo]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(' ');
  const ukon = usedAsTitle(ukonText(event)) ? '' : ukonText(event);
  const parts = [vehicle, ukon].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/**
 * Build the legacy "created by" line for the event detail — mirrors
 * `src/servis/servis_utils.js:63` ("Vytvořil {jméno}, dd.mm.yyyy v hh:mm").
 * Returns `null` when there's no creation timestamp to show.
 */
export function formatCreatedBy(
  detail: Pick<ServisEventDetail, 'creator_jmeno' | 'creator_prijmeni' | 'datum_vytvoreni'>,
): string | null {
  const d = parseDateTime(detail.datum_vytvoreni);
  if (!d) return null;
  const name = [detail.creator_jmeno, detail.creator_prijmeni]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(' ');
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const who = name ? `Vytvořil ${name}` : 'Vytvořeno';
  return `${who} · ${date} v ${time}`;
}

/** Internal-event kinds, in the order the form offers them. */
export const DRUH_UKONU_LABEL: Record<ServisDruhUkonu | 'jine', string> = {
  servis: 'Servisní prohlídka',
  stk: 'STK',
  servis_stk: 'Servisní prohlídka + STK',
  jine: 'Jiné',
};

/** `2026-07-28 08:00:00` → `28. 7. 2026 08:00`; empty → `—`. */
export function formatDatumCas(s: string | null | undefined): string {
  const v = (s ?? '').trim();
  if (!v) return '—';
  const dt = v.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  return dt ? `${parseInt(dt[3], 10)}. ${parseInt(dt[2], 10)}. ${dt[1]} ${dt[4]}:${dt[5]}` : v;
}

/**
 * Whether an event is in progress at `now` — i.e. its [datum_od, datum_do)
 * window straddles the clock. `now` is a server "YYYY-MM-DD HH:MM:SS" string
 * (the week response's `serverNow`); falsy `now` means we can't tell → false.
 */
export function isEventNow(
  event: Pick<ServisEvent, 'datum_od' | 'datum_do'>,
  now: string | null | undefined,
): boolean {
  const n = parseDateTime(now ?? undefined);
  const od = parseDateTime(event.datum_od);
  const dod = parseDateTime(event.datum_do);
  if (!n || !od || !dod) return false;
  return od.getTime() <= n.getTime() && n.getTime() < dod.getTime();
}

/** Returns the time portion HH:MM from a "YYYY-MM-DD HH:MM:SS" string. */
export function eventTimeHM(s: string | null | undefined): string {
  const d = parseDateTime(s ?? undefined);
  if (!d) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Convert the server "YYYY-MM-DD HH:MM:SS" datetime into the value an HTML5
 * `<input type="datetime-local">` expects ("YYYY-MM-DDTHH:MM"). Strips seconds
 * because the legacy form uses minute granularity.
 */
export function toLocalInput(s: string | null | undefined): string {
  if (!s) return '';
  const trimmed = s.trim();
  if (!trimmed) return '';
  // Accept both "YYYY-MM-DD HH:MM:SS" and ISO; output 16-char "YYYY-MM-DDTHH:MM".
  return trimmed.replace(' ', 'T').slice(0, 16);
}

/** Inverse: "YYYY-MM-DDTHH:MM" → "YYYY-MM-DD HH:MM:00" for the API. */
export function fromLocalInput(v: string): string {
  if (!v) return '';
  const t = v.includes('T') ? v.replace('T', ' ') : v;
  return t.length === 16 ? `${t}:00` : t;
}

/** Server "YYYY-MM-DD HH:MM:SS" → ISO-week (1..53) + ISO-year. */
export function isoWeekFromDateTime(s: string): { week: number; year: number } | null {
  const d = parseDateTime(s);
  if (!d) return null;
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  target.setHours(0, 0, 0, 0);
  // ISO Thursday trick — same as servis.ts on the backend.
  target.setDate(target.getDate() + 3 - ((target.getDay() + 6) % 7));
  const isoYear = target.getFullYear();
  const firstThursday = new Date(isoYear, 0, 4);
  firstThursday.setHours(0, 0, 0, 0);
  firstThursday.setDate(
    firstThursday.getDate() + 3 - ((firstThursday.getDay() + 6) % 7),
  );
  const diffDays = Math.round(
    (target.getTime() - firstThursday.getTime()) / 86_400_000,
  );
  return { week: 1 + Math.floor(diffDays / 7), year: isoYear };
}

export function mechanicFirstLast(jmeno: string): { first: string; last: string } {
  const parts = jmeno.trim().split(/\s+/);
  if (parts.length === 0) return { first: jmeno, last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

/**
 * Build the `datetime-local` value ("YYYY-MM-DDTHH:MM") for a clicked grid slot.
 * `slotIndex` is 0-based within the 7:00–17:00 window (matches SLOT_LABELS).
 */
export function slotToLocalInput(dateStr: string, slotIndex: number): string {
  const totalMinutes = SLOT_START_HOUR * 60 + slotIndex * 30;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${dateStr}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Whether a grid slot starts at or before the server clock — i.e. you can't
 * schedule into it. Mirrors the legacy gate (`!serverNow || slotStr > serverNow`
 * in `src/servis/servis_utils.js`): with no `serverNow` known, nothing is past.
 */
export function isSlotInPast(
  dateStr: string,
  slotIndex: number,
  serverNow: string | null | undefined,
): boolean {
  if (!serverNow) return false;
  const slot = parseDateTime(slotToLocalInput(dateStr, slotIndex));
  const now = parseDateTime(serverNow);
  if (!slot || !now) return false;
  return slot.getTime() <= now.getTime();
}

/** Add `minutes` to a "YYYY-MM-DDTHH:MM" local value; returns the same format. */
export function addMinutesLocal(local: string, minutes: number): string {
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return local;
  d.setMinutes(d.getMinutes() + minutes);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ============================================================================
// Phone agenda grouping (Phase 14.6)
//
// The agenda lists a single day's events chronologically, grouped under hour
// dividers. Times are clamped to the 7:00–17:00 slot window the same way the
// swimlane clamps chips, so a multi-day event continuing from yesterday files
// under the day's first slot instead of its literal (previous-day) start.
// ============================================================================

export interface AgendaHourGroup {
  /** Hour divider label, zero-padded ("07:00"). */
  hour: string;
  items: { event: ServisEvent; time: string }[];
}

function slotTimeLabel(slot: number): string {
  const h = SLOT_START_HOUR + Math.floor(slot / 2);
  const m = slot % 2 === 0 ? '00' : '30';
  return `${String(h).padStart(2, '0')}:${m}`;
}

/**
 * Group a day's events into hour buckets for the phone agenda. Events outside
 * the visible slot window are dropped (same rule as the swimlane); a non-empty
 * `mechanicIds` filter keeps only those technicians' events.
 */
export function agendaHourGroups(
  day: ServisDay,
  mechanicIds?: Set<number>,
): AgendaHourGroup[] {
  const ranged = day.events
    .filter((e) => !mechanicIds || mechanicIds.size === 0 || mechanicIds.has(e.id_opravce))
    .map((event) => ({ event, range: eventSlotRange(event, day) }))
    .filter((x): x is { event: ServisEvent; range: { start: number; span: number } } =>
      x.range !== null,
    )
    .sort(
      (a, b) =>
        a.range.start - b.range.start ||
        a.event.datum_od.localeCompare(b.event.datum_od),
    );

  const groups: AgendaHourGroup[] = [];
  for (const { event, range } of ranged) {
    const hour = `${slotTimeLabel(range.start).slice(0, 2)}:00`;
    const last = groups[groups.length - 1];
    const item = { event, time: slotTimeLabel(range.start) };
    if (last && last.hour === hour) last.items.push(item);
    else groups.push({ hour, items: [item] });
  }
  return groups;
}

// ============================================================================
// Datum rules the native <input type="datetime-local"> can't express but the
// legacy flatpickr enforced: 30-min grid, working-hours window per field, and
// no past scheduling on create (edit is exempt — existing events may be old).
// Pure + zod-free so the modal can map the result onto its zod superRefine and
// these stay unit-testable in isolation.
// ============================================================================

/** Per-field working-hours window (minutes since midnight). The working day is
 * 7:00–17:00 (SLOT_START_HOUR/SLOT_END_HOUR); a start can be any slot that
 * still leaves room for one 30-min block (7:00–16:30) and an end any slot from
 * the earliest possible (7:30) to the close (17:00). The legacy flatpickr
 * clipped these to 7:00–16:00 / 8:00–17:00, which forbade legitimate edge-of-
 * day half-hour jobs (16:30–17:00, 7:00–7:30) for no functional reason. */
export const WORK_HOURS = {
  datum_od: { min: 7 * 60, max: 16 * 60 + 30, label: 'Začátek' },
  datum_do: { min: 7 * 60 + 30, max: 17 * 60, label: 'Konec' },
} as const;

export interface DatumIssue {
  field: 'datum_od' | 'datum_do';
  message: string;
}

function fmtHm(mins: number): string {
  return `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}`;
}

function issuesForField(
  field: 'datum_od' | 'datum_do',
  value: string,
  isEdit: boolean,
  now: number,
): DatumIssue[] {
  // Wait for a full "YYYY-MM-DDTHH:MM" value; required-ness + the start<end
  // ordering are checked elsewhere.
  if (!value || value.length < 16) return [];
  const h = Number(value.slice(11, 13));
  const m = Number(value.slice(14, 16));
  if (Number.isNaN(h) || Number.isNaN(m)) return [];

  const out: DatumIssue[] = [];
  const cfg = WORK_HOURS[field];

  if (m !== 0 && m !== 30) {
    out.push({ field, message: 'Čas musí být v celých půlhodinách (např. 8:00 nebo 8:30).' });
  }
  const tod = h * 60 + m;
  if (tod < cfg.min || tod > cfg.max) {
    out.push({ field, message: `${cfg.label} musí být mezi ${fmtHm(cfg.min)} a ${fmtHm(cfg.max)}.` });
  }
  if (!isEdit) {
    const when = new Date(value).getTime();
    if (!Number.isNaN(when) && when < now) {
      out.push({ field, message: 'Termín nesmí být v minulosti.' });
    }
  }
  return out;
}

/**
 * Validate both endpoints of a {datum_od, datum_do} servis form against the
 * working-hours / half-hour / no-past rules. `now` is injectable for tests;
 * defaults to `Date.now()`.
 */
export function datumIssues(
  datumOd: string,
  datumDo: string,
  isEdit: boolean,
  now: number = Date.now(),
): DatumIssue[] {
  return [
    ...issuesForField('datum_od', datumOd, isEdit, now),
    ...issuesForField('datum_do', datumDo, isEdit, now),
  ];
}

// ============================================================================
// Overlap / conflict preview
//
// Mirrors the server's `ServisRepository.resolveConflicts()` so the modal can
// warn *before* submit. The server silently splits / truncates existing events
// (Cases 2–4) and only hard-errors when the new event fully swallows an
// existing one (Case 1) — so surfacing these is a data-safety requirement, not
// just a convenience. Shifts (typ_udalosti === 3) are excluded server-side and
// here too.
// ============================================================================

export type ConflictKind = 'block' | 'split' | 'truncate';

export interface ConflictImpact {
  kind: ConflictKind;
  event: ServisEvent;
}

/**
 * Given a mechanic's existing events and a proposed range, classify how each
 * overlapping event would be affected. `proposedOd` / `proposedDo` are
 * `datetime-local` strings ("YYYY-MM-DDTHH:MM"); `excludeId` skips the event
 * being edited.
 */
export function computeConflicts(
  existing: ServisEvent[],
  proposedOd: string,
  proposedDo: string,
  excludeId?: number | null,
): ConflictImpact[] {
  const newStart = new Date(proposedOd).getTime();
  const newEnd = new Date(proposedDo).getTime();
  if (Number.isNaN(newStart) || Number.isNaN(newEnd) || newEnd <= newStart) {
    return [];
  }

  const impacts: ConflictImpact[] = [];
  for (const ev of existing) {
    if (ev.typ_udalosti === 3) continue;
    if (excludeId != null && ev.id_udalosti === excludeId) continue;

    const od = parseDateTime(ev.datum_od);
    const dod = parseDateTime(ev.datum_do);
    if (!od || !dod) continue;
    const existStart = od.getTime();
    const existEnd = dod.getTime();

    // Only genuine (non-adjacent) overlaps matter.
    if (existStart >= newEnd || existEnd <= newStart) continue;

    if (
      (existStart > newStart && existEnd < newEnd) ||
      (existStart === newStart && existEnd === newEnd)
    ) {
      impacts.push({ kind: 'block', event: ev });
    } else if (existStart < newStart && existEnd > newEnd) {
      impacts.push({ kind: 'split', event: ev });
    } else {
      impacts.push({ kind: 'truncate', event: ev });
    }
  }
  return impacts;
}

// ============================================================================
// Vehicle identity — every event carries the car's SPZ or VIN (at least one)
// ============================================================================

/** VIN as typed: no whitespace, upper-case, at most 17 characters. */
export function normalizeVinInput(value: string): string {
  return value.replace(/\s+/g, '').toUpperCase().slice(0, 17);
}

/** A complete VIN — 17 characters, no I / O / Q. */
export function looksLikeVin(value: string): boolean {
  return /^[A-HJ-NPR-Z0-9]{17}$/i.test(value.trim());
}

/** An event needs the plate or the VIN of its car — one is enough. */
export function hasVehicleIdentity(spz: string, vin: string): boolean {
  return spz.trim() !== '' || vin.trim() !== '';
}

/**
 * Which deadlines of the car an úkon of this kind takes care of when it is
 * confirmed as done — mirrors `terminyProDruh` on the API.
 */
export function terminyDruhu(druh: ServisDruhUkonu | null | undefined): Array<'servis' | 'stk'> {
  if (druh === 'servis') return ['servis'];
  if (druh === 'stk') return ['stk'];
  if (druh === 'servis_stk') return ['servis', 'stk'];
  return [];
}

/** Whole months in 1–120 from typed text, else null. */
export function parseMesice(text: string): number | null {
  const t = text.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n >= 1 && n <= 120 ? n : null;
}

/** Brand + model, falling back to the production / order identifier when unnamed. */
export function vehicleLabel(znacka: string | null, model: string | null, fallback: string): string {
  const named = [znacka, model].filter(Boolean).join(' ').trim();
  return named || fallback || '—';
}

/**
 * A nové → ojeté hand-over copies the car, so the customer's cars can list it
 * twice under one VIN. The ojeté row is the live one — keep it, drop the nové.
 * A blank VIN never matches (unrelated cars without one stay separate).
 */
export function dropMovedNove<N extends { vin: string | null }, O extends { vin: string | null }>(
  nove: N[],
  ojete: O[],
): N[] {
  const key = (vin: string | null) => (vin ?? '').trim().toUpperCase();
  const moved = new Set(ojete.map((o) => key(o.vin)).filter(Boolean));
  return nove.filter((n) => !moved.has(key(n.vin)));
}
